import { useState, useMemo } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { useToast } from './useToast';
import { useConfirm } from './useConfirm';
import { formatDateLocal } from '../utils/dateUtils';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import type {
  Employee,
  Project,
  Assignment,
  DiversSubProject,
  DiversFormData,
  Period,
  PDFRowData,
} from './useSchedulerData';
import {
  LEAVE_LABELS,
  SIEGE_COORDS,
  calculateDistance,
  determineZone,
} from './useSchedulerData';

interface SchedulerData {
  employees: Employee[];
  rowEmployees: Employee[];
  setRowEmployees: React.Dispatch<React.SetStateAction<Employee[]>>;
  projects: Project[];
  assignments: Assignment[];
  setAssignments: React.Dispatch<React.SetStateAction<Assignment[]>>;
  leaves: { employee_id: string; start_date: string; end_date: string; type: string }[];
  trainingDays: { employee_id: string; day: string; description: string }[];
  validationInfo: { status: 'draft' | 'published'; created_at: string } | null;
  setValidationInfo: React.Dispatch<React.SetStateAction<{ status: 'draft' | 'published'; created_at: string } | null>>;
  diversSubProjects: DiversSubProject[];
  setDiversSubProjects: React.Dispatch<React.SetStateAction<DiversSubProject[]>>;
  diversGroups: Project[];
  startOfWeek: Date;
  weekDays: Date[];
  currentWeekNumber: number;
  currentYear: number;
  isPublished: boolean;
  loading: boolean;
  fetchData: () => Promise<void>;
}

export function useSchedulerActions(data: SchedulerData) {
  const { user } = useAuth();
  const { toast } = useToast();
  const { confirm: confirmDialog } = useConfirm();

  // Tool state
  const [selectedProjectId, setSelectedProjectId] = useState<string | 'eraser' | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Divers modal state
  const [showDiversModal, setShowDiversModal] = useState(false);
  const [diversForm, setDiversForm] = useState<DiversFormData>({ divers_number: '', name: '', address: '', parent_project_id: '' });
  const [isGeocoding, setIsGeocoding] = useState(false);
  const [isSavingDivers, setIsSavingDivers] = useState(false);

  // Sub-project picker state
  const [showSubProjectPicker, setShowSubProjectPicker] = useState<{ projectId: string; parentEntity: string } | null>(null);
  const [selectedSubProjectId, setSelectedSubProjectId] = useState<string | null>(null);

  // Drag-and-drop state
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  // WAG group selected memo
  const isWagGroupSelected = useMemo(() => {
    const sel = data.diversGroups.find(g => g.id === diversForm.parent_project_id);
    return sel?.name?.toUpperCase().includes('WAG') ?? false;
  }, [data.diversGroups, diversForm.parent_project_id]);

  // --- ASSIGNMENT ---
  const handleAssign = async (employeeId: string, dateStr: string, targetPeriod: Period) => {
    if (!selectedProjectId) return;
    if (data.loading || isSubmitting) return;

    if (data.isPublished) {
      toast.warning("Ce planning est publié. Repassez-le en brouillon pour le modifier.");
      return;
    }

    const isLeave = data.leaves.find(l => l.employee_id === employeeId && l.start_date <= dateStr && l.end_date >= dateStr);
    if (isLeave) {
      toast.warning(`Impossible : Employé en ${LEAVE_LABELS[isLeave.type] || 'congé'}.`);
      return;
    }

    const isTraining = data.trainingDays.find(t => t.employee_id === employeeId && t.day === dateStr);
    if (isTraining) {
      toast.warning(`Impossible : Employé en FORMATION (${isTraining.description}).`);
      return;
    }

    setIsSubmitting(true);

    try {
      if (selectedProjectId === 'eraser') {
        data.setAssignments(prev => prev.filter(a => !(a.employee_id === employeeId && a.assignment_date === dateStr && a.period === targetPeriod)));

        const { error } = await supabase
          .from('project_assignments')
          .delete()
          .match({ employee_id: employeeId, assignment_date: dateStr, period: targetPeriod });

        if (error) throw error;
      } else {
        const blockingDayEntry = data.assignments.find(a =>
          a.employee_id === employeeId && a.assignment_date === dateStr && a.period === 'DAY'
        );

        if (blockingDayEntry && blockingDayEntry.id) {
          await supabase.from('project_assignments').delete().eq('id', blockingDayEntry.id);
        }

        const newAssignment: Assignment = {
          employee_id: employeeId,
          project_id: selectedProjectId,
          assignment_date: dateStr,
          period: targetPeriod,
          divers_sub_project_id: selectedSubProjectId || null
        };

        data.setAssignments(prev => {
          const cleanPrev = prev.filter(a =>
            !(a.employee_id === employeeId && a.assignment_date === dateStr && a.period === targetPeriod) &&
            !(a.employee_id === employeeId && a.assignment_date === dateStr && a.period === 'DAY')
          );
          return [...cleanPrev, newAssignment];
        });

        const { error } = await supabase
          .from('project_assignments')
          .upsert(newAssignment, { onConflict: 'employee_id, assignment_date, period' });

        if (error) throw error;
      }
    } catch (error) {
      console.error("ERREUR AFFECTATION:", error);
      data.fetchData();
    } finally {
      setIsSubmitting(false);
    }
  };

  // --- SAVE / PUBLISH ---
  const handleSaveDraft = async () => {
    if (!user) return;
    if (data.isPublished) {
      const ok = await confirmDialog({ title: 'Repasser en brouillon', message: "Le planning est actuellement PUBLIÉ. Le repasser en BROUILLON le rendra invisible pour les employés. Continuer ?", variant: 'warning' });
      if (!ok) return;
    }

    setIsSubmitting(true);
    try {
      const { error } = await supabase.from('planning_validations').upsert({
        year: data.currentYear,
        week_number: data.currentWeekNumber,
        validated_by: user.id,
        status: 'draft',
        created_at: new Date().toISOString()
      }, { onConflict: 'year, week_number' });

      if (error) throw error;

      data.setValidationInfo({ status: 'draft', created_at: new Date().toISOString() });
      toast.success("Planning enregistré en BROUILLON.");

    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de la sauvegarde.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleValidateWeek = async () => {
    if (!user) return;
    const ok = await confirmDialog({ title: 'Publier le planning', message: "Confirmer la publication du planning ?", variant: 'warning' });
    if (!ok) return;

    try {
      const { error } = await supabase.from('planning_validations').upsert({
        year: data.currentYear,
        week_number: data.currentWeekNumber,
        validated_by: user.id,
        status: 'published',
        created_at: new Date().toISOString()
      }, { onConflict: 'year, week_number' });

      if (error) throw error;

      data.setValidationInfo({ status: 'published', created_at: new Date().toISOString() });
      toast.success("Planning PUBLIÉ avec succès !");
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de la publication.");
    }
  };

  // --- PDF EXPORT ---
  const handleExportPDF = () => {
    const doc = new jsPDF('l', 'mm', 'a3');
    doc.setFontSize(22);
    doc.setTextColor(40);
    doc.text(`PLANNING SEMAINE ${data.currentWeekNumber} (${data.currentYear})`, 14, 15);
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(`État : ${data.isPublished ? 'PUBLIÉ' : 'BROUILLON'} - Exporté le ${new Date().toLocaleDateString()}`, 14, 22);

    const columns = [
      { header: 'COLLABORATEUR', dataKey: 'name' },
      ...data.weekDays.map(d => ({
        header: d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric' }).toUpperCase(),
        dataKey: formatDateLocal(d)
      }))
    ];

    const rows = data.rowEmployees.map(emp => {
      const rowData: PDFRowData = { name: emp.full_name };
      data.weekDays.forEach(day => {
        const dStr = formatDateLocal(day);
        const leave = data.leaves.find(l => l.employee_id === emp.id && l.start_date <= dStr && l.end_date >= dStr);
        const training = data.trainingDays.find(t => t.employee_id === emp.id && t.day === dStr);

        if (training) {
          rowData[dStr] = `FORMATION\n${training.description}`;
        } else if (leave) {
          rowData[dStr] = (LEAVE_LABELS[leave.type] || leave.type).toUpperCase();
        } else {
          const getProjName = (p: Period) => {
            const assign = data.assignments.find(a => a.employee_id === emp.id && a.assignment_date === dStr && a.period === p);
            if (!assign) return null;
            const proj = data.projects.find(pr => pr.id === assign.project_id);
            return proj ? proj.name : null;
          };

          const p1 = getProjName('Q1');
          const p2 = getProjName('Q2');
          const p3 = getProjName('Q3');
          const p4 = getProjName('Q4');

          let morningStr = '';
          if (p1 && p2 && p1 === p2) morningStr = p1;
          else if (p1 || p2) morningStr = [p1, p2].filter(Boolean).join(' / ');

          let afternoonStr = '';
          if (p3 && p4 && p3 === p4) afternoonStr = p3;
          else if (p3 || p4) afternoonStr = [p3, p4].filter(Boolean).join(' / ');

          if (!morningStr && !afternoonStr) {
            rowData[dStr] = '';
          } else if (morningStr === afternoonStr && morningStr !== '') {
            rowData[dStr] = `${morningStr} (Journée)`;
          } else {
            const mPart = morningStr ? `M: ${morningStr}` : '';
            const aPart = afternoonStr ? `A: ${afternoonStr}` : '';
            rowData[dStr] = [mPart, aPart].filter(Boolean).join('\n');
          }
        }
      });
      return rowData;
    });

    autoTable(doc, {
      columns, body: rows, startY: 30, theme: 'grid',
      headStyles: { fillColor: [0, 133, 66], textColor: 255, fontSize: 10, halign: 'center', valign: 'middle' },
      styles: { fontSize: 8, cellPadding: 3, valign: 'middle', overflow: 'linebreak', halign: 'center' },
      columnStyles: { name: { fontStyle: 'bold', halign: 'left', fillColor: [245, 245, 245], cellWidth: 50 } },
      didParseCell: (hookData) => {
        if (hookData.section === 'body' && hookData.column.dataKey !== 'name') {
          const text = (hookData.cell.raw as string) || '';
          if (text.includes('CONGÉS') || text.includes('MALADIE') || text.includes('SANS SOLDE')) {
            hookData.cell.styles.fillColor = [240, 240, 240];
            hookData.cell.styles.textColor = [150, 150, 150];
            hookData.cell.styles.fontStyle = 'italic';
          }
        }
      }
    });
    doc.save(`planning_S${data.currentWeekNumber}_${data.currentYear}.pdf`);
  };

  // --- DIVERS MODAL ---
  const handleDiversAddressSearch = async () => {
    if (!diversForm.address || diversForm.address.length < 3) return;

    setIsGeocoding(true);
    try {
      const response = await fetch(`https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(diversForm.address)}&limit=1`);
      if (!response.ok) throw new Error("Erreur réseau API Adresse");

      const fetchData = await response.json();

      if (fetchData.features && fetchData.features.length > 0) {
        const feature = fetchData.features[0];
        const [lng, lat] = feature.geometry.coordinates;
        const label = feature.properties.label;

        const distance = calculateDistance(SIEGE_COORDS.lat, SIEGE_COORDS.lng, lat, lng);
        const zone = determineZone(distance);

        setDiversForm(prev => ({
          ...prev,
          address: label,
          lat: lat,
          lng: lng,
          zone: zone
        }));
      } else {
        toast.warning("Adresse introuvable. Veuillez vérifier la saisie.");
      }
    } catch (error) {
      console.error("Erreur géocodage:", error);
      toast.error("Erreur technique lors de la recherche d'adresse.");
    } finally {
      setIsGeocoding(false);
    }
  };

  const handleCreateDivers = async () => {
    if (!diversForm.parent_project_id) {
      toast.warning("Veuillez sélectionner un groupe Divers de rattachement.");
      return;
    }
    const parentGroup = data.diversGroups.find(g => g.id === diversForm.parent_project_id);
    if (!isWagGroupSelected && !diversForm.divers_number.trim()) {
      toast.warning("Veuillez saisir un numéro Divers.");
      return;
    }
    if (!diversForm.name.trim()) {
      toast.warning("Veuillez saisir un nom pour le chantier.");
      return;
    }

    setIsSavingDivers(true);
    try {
      if (!parentGroup) {
        throw new Error("Groupe Divers non trouvé.");
      }

      const { data: newSubProject, error: subError } = await supabase
        .from('divers_sub_projects')
        .insert({
          parent_project_id: diversForm.parent_project_id,
          divers_number: diversForm.divers_number.trim(),
          name: diversForm.name.trim(),
          address: diversForm.address || null,
          lat: diversForm.lat || null,
          lng: diversForm.lng || null,
          zone: diversForm.zone || null,
          is_active: true
        })
        .select()
        .single();

      if (subError) throw subError;

      if (newSubProject) {
        data.setDiversSubProjects(prev => [...prev, newSubProject as DiversSubProject]);
        setSelectedSubProjectId(newSubProject.id);
      }

      setSelectedProjectId(diversForm.parent_project_id);

      setShowDiversModal(false);
      setDiversForm({ divers_number: '', name: '', address: '', parent_project_id: '' });

      const label = diversForm.divers_number.trim()
        ? `${diversForm.divers_number} - ${diversForm.name}`
        : diversForm.name;
      toast.success(`Sous-chantier "${label}" créé avec succès ! Rattaché à : ${parentGroup.name}`);

    } catch (error) {
      console.error("Erreur création Divers:", error);
      toast.error("Erreur lors de la création du chantier.");
    } finally {
      setIsSavingDivers(false);
    }
  };

  // --- SUB-PROJECT SELECTION ---
  const handleProjectSelect = (proj: Project) => {
    if (selectedProjectId === proj.id) {
      setSelectedProjectId(null);
      setSelectedSubProjectId(null);
      return;
    }
    if (proj.category === 'divers') {
      const subProjects = data.diversSubProjects.filter(sp => sp.parent_project_id === proj.id);
      if (subProjects.length > 0) {
        const entityLabel = proj.parent_entity || (proj.name.toUpperCase().includes('WAG') ? 'WAG' : proj.name);
        setShowSubProjectPicker({ projectId: proj.id, parentEntity: entityLabel });
      } else {
        setSelectedProjectId(proj.id);
        setSelectedSubProjectId(null);
      }
    } else {
      setSelectedProjectId(proj.id);
      setSelectedSubProjectId(null);
    }
  };

  const handleSubProjectSelect = (subProject: DiversSubProject) => {
    setSelectedProjectId(showSubProjectPicker?.projectId || null);
    setSelectedSubProjectId(subProject.id);
    setShowSubProjectPicker(null);
  };

  const getSelectedDisplayName = () => {
    if (!selectedProjectId || selectedProjectId === 'eraser') return null;
    const proj = data.projects.find(p => p.id === selectedProjectId);
    if (!proj) return null;

    if (selectedSubProjectId) {
      const subProj = data.diversSubProjects.find(sp => sp.id === selectedSubProjectId);
      if (subProj) {
        return `${subProj.divers_number} - ${subProj.name}`;
      }
    }
    return proj.name;
  };

  // --- DRAG-AND-DROP ---
  const handleDragStart = (e: React.DragEvent, index: number) => {
    setIsDragging(true);
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', index.toString());
    const dragImage = document.createElement('div');
    dragImage.textContent = data.rowEmployees[index]?.full_name || '';
    dragImage.style.cssText = 'position: absolute; top: -1000px; padding: 8px 16px; background: #2563eb; color: white; border-radius: 8px; font-weight: 600;';
    document.body.appendChild(dragImage);
    e.dataTransfer.setDragImage(dragImage, 0, 0);
    setTimeout(() => document.body.removeChild(dragImage), 0);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (draggedIndex !== null && draggedIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === dropIndex) {
      resetDragState();
      return;
    }

    const newRowEmployees = [...data.rowEmployees];
    const [draggedEmployee] = newRowEmployees.splice(draggedIndex, 1);
    newRowEmployees.splice(dropIndex, 0, draggedEmployee);
    data.setRowEmployees(newRowEmployees);

    const orderIds = newRowEmployees.map(emp => emp.id);
    const weekKey = `employee_display_order_${data.currentYear}_S${data.currentWeekNumber}`;
    localStorage.setItem(weekKey, JSON.stringify(orderIds));

    resetDragState();
  };

  const handleDragEnd = () => {
    resetDragState();
  };

  const resetDragState = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
    setIsDragging(false);
  };

  // --- RENDER CELL BLOCK (inline component) ---
  const RenderCellBlock = ({ project, assignment, periods, empId, dateStr, isMerged = false }: { project: Project | null, assignment?: Assignment | null, periods: Period[], empId: string, dateStr: string, isMerged?: boolean }) => {
    const cursorStyle = data.isPublished ? 'not-allowed' : 'pointer';

    const handleClick = () => {
      periods.forEach(p => handleAssign(empId, dateStr, p));
    };

    let displayName = project?.name || '';
    let displayCode = project?.code || '';

    if (assignment?.divers_sub_project_id) {
      const subProj = data.diversSubProjects.find(sp => sp.id === assignment.divers_sub_project_id);
      if (subProj) {
        displayName = subProj.name;
        displayCode = subProj.divers_number;
      }
    }

    const startPeriod = periods[0];
    let timeLabel = "";
    let colorCode = "#94a3b8";

    if (startPeriod === 'Q1') { timeLabel = "08h"; colorCode = "#3b82f6"; }
    else if (startPeriod === 'Q2') { timeLabel = "12h"; colorCode = "#d97706"; }
    else if (startPeriod === 'Q3') { timeLabel = "13h"; colorCode = "#6366f1"; }
    else if (startPeriod === 'Q4') { timeLabel = "17h"; colorCode = "#8b5cf6"; }

    if (isMerged) {
      if (startPeriod === 'Q1') { timeLabel = "Matin"; }
      if (startPeriod === 'Q3') { timeLabel = "Aprem"; }
    }

    const bgColor = project ? `${project.color}26` : 'white';
    const textColor = project ? project.color : colorCode;

    return (
      <div
        onClick={handleClick}
        style={{
          flex: 1,
          borderRight: (!isMerged && periods[0] !== 'Q4' && periods[0] !== 'Q2') ? '1px dotted #e2e8f0' : 'none',
          cursor: cursorStyle,
          background: bgColor,
          display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative',
          color: textColor,
          height: '100%',
          flexDirection: 'column',
          padding: '4px',
          transition: 'all 0.1s ease'
        }}
      >
        {!project && !data.isPublished && selectedProjectId && (
          <div style={{
            fontSize: isMerged ? '0.75rem' : '0.7rem',
            fontWeight: '600',
            color: colorCode,
            border: `1px solid ${colorCode}30`,
            padding: '1px 6px',
            borderRadius: '6px',
            backgroundColor: 'white'
          }}>
            {timeLabel}
          </div>
        )}

        {project && (
          <div style={{ textAlign: 'center', lineHeight: 1.1, width: '100%', overflow: 'hidden' }}>
            <div style={{ fontWeight: '800', fontSize: isMerged ? '0.9rem' : '0.7rem', color: project.color, textTransform: 'uppercase', marginBottom: '2px' }}>
              {displayName}
            </div>
            {isMerged && (
              <div style={{ fontSize: '0.65rem', opacity: 0.8, color: project.color }}>
                {displayCode}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return {
    // Tool state
    selectedProjectId, setSelectedProjectId,
    isSubmitting,
    // Divers modal
    showDiversModal, setShowDiversModal,
    diversForm, setDiversForm,
    isGeocoding,
    isSavingDivers,
    isWagGroupSelected,
    // Sub-project picker
    showSubProjectPicker, setShowSubProjectPicker,
    selectedSubProjectId, setSelectedSubProjectId,
    // Drag-and-drop
    draggedIndex, dragOverIndex, isDragging,
    // Actions
    handleAssign,
    handleSaveDraft,
    handleValidateWeek,
    handleExportPDF,
    handleProjectSelect,
    handleSubProjectSelect,
    getSelectedDisplayName,
    handleDiversAddressSearch,
    handleCreateDivers,
    handleDragStart,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleDragEnd,
    // Component
    RenderCellBlock,
  };
}
